// src/sections/Contact.jsx
import * as React from "react";
import {
  Alert,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

import Section from "../components/ui/Section";
import SectionHeader from "../components/ui/SectionHeader";
import { headers } from "../content/meta";
import { CONTACT } from "../config/site";
import { contactContent as C } from "../content/contact";

export default function Contact() {
  const [form, setForm] = React.useState({
    name: "",
    phone: "",
    company: "",
    message: "",
  });

  const [alert, setAlert] = React.useState({
    open: false,
    type: "info",
    text: "",
  });

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const isValid =
    form.name.trim().length >= (C.form.min?.name ?? 2) &&
    form.phone.trim().length >= (C.form.min?.phone ?? 8) &&
    form.message.trim().length >= (C.form.min?.message ?? 5);

  const buildMessage = () =>
    `الاسم: ${form.name}\nالهاتف: ${form.phone}\nالشركة: ${
      form.company || "-"
    }\n\nالرسالة:\n${form.message}`;

  const sendWhatsApp = () =>
    window.open(
      `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(
        buildMessage()
      )}`,
      "_blank",
      "noopener"
    );

  return (
    <Section id={C.sectionId}>
      <SectionHeader {...headers.contact} />

      <Grid container spacing={2} alignItems="stretch">
        {/* فورم الإرسال إلى الـ API (نفس التصميم) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: 3,
              height: "100%",
              display: "grid",
              gap: 1.5,
            }}
          >
            <Typography sx={{ fontWeight: 900 }}>{C.form.title}</Typography>

            <TextField
              label={C.form.labels.name}
              name="name"
              value={form.name}
              onChange={onChange}
              required
              fullWidth
              size="medium"
            />
            <TextField
              label={C.form.labels.phone}
              name="phone"
              value={form.phone}
              onChange={onChange}
              required
              fullWidth
              size="medium"
              inputMode="tel"
            />
            <TextField
              label={C.form.labels.company}
              name="company"
              value={form.company}
              onChange={onChange}
              fullWidth
              size="medium"
            />
            <TextField
              label={C.form.labels.message}
              name="message"
              value={form.message}
              onChange={onChange}
              required
              fullWidth
              size="medium"
              multiline
              minRows={4}
            />

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 1 }}
            >
              {/* زر الإرسال إلى الـ API بدل البريد */}
              {/* خيار واتساب كما هو (ثانوي) */}
              <Button
                variant="outlined"
                color="success"
                startIcon={<WhatsAppIcon />}
                disabled={!isValid}
                onClick={sendWhatsApp}
                sx={{
                  fontWeight: 800,
                  borderColor: "success.main",
                  color: "success.contrastText",
                  bgcolor: "success.main",
                  "&:hover": {
                    bgcolor: "success.dark",
                    borderColor: "success.dark",
                  },
                }}
              >
                {C.form.ctaWhatsapp}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        {/* معلومات الاتصال (نفس التصميم) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: 3,
              height: "100%",
              display: "grid",
              alignContent: "start",
              gap: 1.25,
            }}
          >
            <Typography sx={{ fontWeight: 900 }}>{C.info.title}</Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <WhatsAppIcon color="success" />
              <Typography fontWeight={800}>
                واتساب: +{CONTACT.whatsapp}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <PhoneRoundedIcon color="primary" />
              <Typography fontWeight={800}>اتصال: +{CONTACT.phone}</Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <EmailRoundedIcon color="primary" />
              <Typography fontWeight={800} sx={{ direction: "ltr" }}>
                {CONTACT.email}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeRoundedIcon color="primary" />
              <Typography color="text.secondary">{C.info.hours}</Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <LocationOnRoundedIcon color="primary" />
              <Typography color="text.secondary">{C.info.location}</Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 0.5 }}
            >
              {C.info.chips.map((label) => (
                <Chip key={label} label={label} variant="outlined" />
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* تنبيهات */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setAlert((s) => ({ ...s, open: false }))}
          severity={alert.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alert.text}
        </Alert>
      </Snackbar>
    </Section>
  );
}
